import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ModalKeyboardDirective, OverflowMenuButtonComponent } from '@clinic-platform/ui-components';

import { CompaniesService } from '../../features/companies/data-access/companies.service';
import { CompanyItem } from '../../features/companies/data-access/companies.models';
import { SettingsService } from '../../features/settings/data-access/settings.service';
import { ClinicOperationalSettingsDto } from '../../features/settings/data-access/settings.models';
import { AuthService } from '../auth/auth.service';
import { SystemAdminContextService } from '../context/system-admin-context.service';

@Component({
  selector: 'app-shell-context-fab',
  standalone: true,
  imports: [OverflowMenuButtonComponent, ModalKeyboardDirective],
  template: `
    @if (canManageHeaderContext) {
      <div class="shell-context-fab">
        <div class="shell-context-fab__button-wrap" [class.shell-context-fab__button-wrap--required]="showContextAttentionState">
          <lib-overflow-menu-button
            [ariaLabel]="contextTriggerLabel"
            [isOpen]="isContextModalOpen"
            (buttonClicked)="toggleContextModal()"
          />
        </div>
      </div>
    }

    @if (isContextModalOpen) {
      <div class="shell-context-fab__modal-backdrop" (click)="closeContextModal()">
        <div
          class="shell-context-fab__modal"
          (click)="$event.stopPropagation()"
          [libModalKeyboard]="true"
          [libModalKeyboardCanSubmit]="false"
          (libModalKeyboardCancel)="closeContextModal()"
        >
          <div class="shell-context-fab__modal-head">
            <div>
              <h3 class="shell-context-fab__modal-title">{{ contextModalTitle }}</h3>
              <p class="shell-context-fab__modal-subtitle">{{ contextModalSubtitle }}</p>
            </div>

            <button
              type="button"
              class="shell-context-fab__modal-close"
              (click)="closeContextModal()"
              aria-label="Close context modal"
            >
              ×
            </button>
          </div>

          <div class="shell-context-fab__modal-grid">
            @if (isSystemAdmin) {
              <label class="shell-context-fab__checkbox">
                <input
                  type="checkbox"
                  [checked]="persistScopeSelection"
                  (change)="onPersistScopeChange(!!$any($event.target).checked)"
                />
                Remember scope
              </label>

              <label class="shell-context-fab__checkbox">
                <input
                  type="checkbox"
                  [checked]="isGlobalScope"
                  (change)="onGlobalScopeChange(!!$any($event.target).checked)"
                />
                Global
              </label>

              <div class="shell-context-fab__field">
                <label class="shell-context-fab__label" for="shell-context-company-select">Company</label>
                <select
                  id="shell-context-company-select"
                  class="shell-context-fab__select"
                  [class.shell-context-fab__select--required]="showCompanyRequiredInHeader"
                  [value]="selectedCompanyId"
                  (change)="onCompanyChange($any($event.target).value)"
                >
                  <option value="">Select company</option>
                  @for (item of companyOptions; track item.companyId) {
                    <option [value]="item.companyId" [selected]="item.companyId === selectedCompanyId">
                      {{ item.slug }}
                    </option>
                  }
                </select>
              </div>
            }

            <div class="shell-context-fab__field">
              <label class="shell-context-fab__label" for="shell-context-clinic-select">Clinic</label>
              <select
                id="shell-context-clinic-select"
                class="shell-context-fab__select"
                [class.shell-context-fab__select--required]="showClinicRequiredInHeader"
                [disabled]="isSystemAdmin && !hasValidSelectedCompany"
                [value]="selectedClinicId"
                (change)="onClinicChange($any($event.target).value)"
              >
                <option value="">{{ clinicSelectEmptyLabel }}</option>
                @for (item of clinicOptions; track item.clinicId) {
                  <option [value]="item.clinicId" [selected]="item.clinicId === selectedClinicId">
                    {{ item.clinicName }}
                  </option>
                }
              </select>
            </div>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .shell-context-fab {
      position: fixed;
      top: 18px;
      right: 20px;
      z-index: 35;
      display: block;
    }

    .shell-context-fab__button-wrap {
      display: inline-flex;
      border-radius: 999px;
    }

    .shell-context-fab__button-wrap--required :is(.overflow-menu-button) {
      border-color: rgba(220, 38, 38, 0.4);
      box-shadow:
        0 0 0 2px rgba(220, 38, 38, 0.12),
        0 10px 24px rgba(15, 23, 42, 0.08);
    }

    .shell-context-fab__modal-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1000;
      display: flex;
      align-items: flex-start;
      justify-content: flex-end;
      padding: 76px 20px 20px;
      background: rgba(15, 23, 42, 0.22);
      backdrop-filter: blur(4px);
    }

    .shell-context-fab__modal {
      width: min(420px, 100%);
      border-radius: 24px;
      border: 1px solid rgba(226, 232, 240, 0.9);
      background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.97));
      box-shadow: 0 24px 80px rgba(15, 23, 42, 0.22);
      padding: 20px;
      box-sizing: border-box;
    }

    .shell-context-fab__modal-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 16px;
    }

    .shell-context-fab__modal-title {
      margin: 0;
      font-size: 22px;
      line-height: 1.1;
      color: #111827;
    }

    .shell-context-fab__modal-subtitle {
      margin: 8px 0 0;
      color: #6b7280;
      font-size: 14px;
      line-height: 1.5;
    }

    .shell-context-fab__modal-close {
      width: 40px;
      height: 40px;
      flex-shrink: 0;
      border: 1px solid #dbe2ea;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.92);
      color: #475569;
      font-size: 24px;
      line-height: 1;
      cursor: pointer;
    }

    .shell-context-fab__modal-grid {
      display: grid;
      gap: 14px;
    }

    .shell-context-fab__checkbox {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      min-height: 40px;
      color: #374151;
      font-size: 13px;
      user-select: none;
    }

    .shell-context-fab__field {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .shell-context-fab__label {
      font-size: 13px;
      color: #374151;
    }

    .shell-context-fab__select {
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 10px;
      padding: 0 12px;
      background: #ffffff;
      color: #111827;
    }

    .shell-context-fab__select--required {
      border-color: #dc2626;
      box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.12);
    }

    @media (max-width: 640px) {
      .shell-context-fab {
        top: 14px;
        right: 16px;
      }

      .shell-context-fab__modal-backdrop {
        padding: 0;
        align-items: flex-end;
      }

      .shell-context-fab__modal {
        width: 100%;
        border-radius: 24px 24px 0 0;
        padding: 16px;
      }
    }
  `]
})
export class ShellContextFabComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly companiesService = inject(CompaniesService);
  private readonly settingsService = inject(SettingsService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly router = inject(Router);
  private readonly subscriptions = new Subscription();
  private currentPath = '';

  protected companyOptions: CompanyItem[] = [];
  protected clinicOptions: ClinicOperationalSettingsDto[] = [];
  protected selectedCompanyId = '';
  protected selectedClinicId = '';
  protected isGlobalScope = false;
  protected persistScopeSelection = false;
  protected isContextModalOpen = false;

  protected get isSystemAdmin(): boolean {
    return this.authService.getCurrentRole() === 'system_admin';
  }

  protected get canManageHeaderContext(): boolean {
    const role = this.authService.getCurrentRole();
    return role === 'system_admin' || role === 'company_admin' || role === 'manager';
  }

  protected get hasValidSelectedCompany(): boolean {
    const selected = this.selectedCompanyId.trim();
    return !!selected && this.companyOptions.some((item) => item.companyId === selected);
  }

  protected get showCompanyRequiredInHeader(): boolean {
    return this.isSystemAdmin
      && this.isCompanyRequiredForCurrentView
      && !this.hasValidSelectedCompany;
  }

  protected get showClinicRequiredInHeader(): boolean {
    return this.isSystemAdmin
      && this.isClinicRequiredForCurrentView
      && this.hasValidSelectedCompany
      && !this.selectedClinicId.trim();
  }

  protected get showContextAttentionState(): boolean {
    return this.showCompanyRequiredInHeader || this.showClinicRequiredInHeader;
  }

  protected get contextTriggerLabel(): string {
    return this.isSystemAdmin ? 'Open scope settings' : 'Open clinic selector';
  }

  protected get contextModalTitle(): string {
    return this.isSystemAdmin ? 'Scope Settings' : 'Clinic Scope';
  }

  protected get contextModalSubtitle(): string {
    return this.isSystemAdmin
      ? 'Switch global, company, and clinic context without crowding the topbar.'
      : 'Choose the active clinic context for operational pages.';
  }

  protected get clinicSelectEmptyLabel(): string {
    return this.isSystemAdmin ? 'All clinics' : 'Select clinic';
  }

  public ngOnInit(): void {
    if (!this.canManageHeaderContext) {
      return;
    }

    this.selectedClinicId = this.systemAdminContext.getClinicId() ?? '';
    this.subscriptions.add(
      this.systemAdminContext.clinicId$.subscribe((clinicId) => {
        this.selectedClinicId = clinicId ?? '';
      }),
    );

    this.currentPath = this.normalizePath(this.router.url);
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => {
          this.currentPath = this.normalizePath(event.urlAfterRedirects);
        }),
    );

    if (this.isSystemAdmin) {
      this.persistScopeSelection = this.systemAdminContext.isPersistenceEnabled();
      this.selectedCompanyId = this.systemAdminContext.getCompanyId() ?? '';
      this.isGlobalScope = this.systemAdminContext.isGlobalScope();
      this.subscriptions.add(
        this.systemAdminContext.persistenceEnabled$.subscribe((enabled) => {
          this.persistScopeSelection = enabled;
        }),
      );
      this.subscriptions.add(
        this.systemAdminContext.scopeMode$.subscribe((mode) => {
          this.isGlobalScope = mode === 'global';
        }),
      );
      this.subscriptions.add(
        this.systemAdminContext.companyId$.subscribe((companyId) => {
          this.selectedCompanyId = companyId ?? '';
        }),
      );
      this.subscriptions.add(
        this.systemAdminContext.refresh$.subscribe(() => {
          this.loadCompanies();
          if (this.hasValidSelectedCompany) {
            this.loadClinics(this.selectedCompanyId);
          } else {
            this.clinicOptions = [];
          }
        }),
      );
      this.loadCompanies();
      if (this.hasValidSelectedCompany) {
        this.loadClinics(this.selectedCompanyId);
      }
      return;
    }

    this.loadTenantClinics();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected toggleContextModal(): void {
    this.isContextModalOpen = !this.isContextModalOpen;
  }

  protected closeContextModal(): void {
    this.isContextModalOpen = false;
  }

  protected onCompanyChange(companyId: string): void {
    const normalized = companyId?.trim() ?? '';
    if (normalized === this.selectedCompanyId) {
      return;
    }

    this.selectedCompanyId = normalized;
    this.systemAdminContext.setCompanyId(normalized || null);
    this.selectedClinicId = '';
    this.systemAdminContext.setClinicId(null);

    if (!normalized) {
      this.clinicOptions = [];
      return;
    }

    this.loadClinics(normalized);
  }

  protected onClinicChange(clinicId: string): void {
    const normalized = clinicId?.trim() ?? '';
    if (normalized === this.selectedClinicId) {
      return;
    }

    this.selectedClinicId = normalized;
    this.systemAdminContext.setClinicId(normalized || null);
  }

  protected onGlobalScopeChange(isGlobal: boolean): void {
    this.isGlobalScope = isGlobal;
    this.systemAdminContext.setScopeMode(isGlobal ? 'global' : 'company');
  }

  protected onPersistScopeChange(enabled: boolean): void {
    this.persistScopeSelection = enabled;
    this.systemAdminContext.setPersistenceEnabled(enabled);
  }

  private loadCompanies(): void {
    const sub = this.companiesService.getList().subscribe({
      next: (items) => {
        this.companyOptions = items;
        const resolvedCompanyId = this.resolvePreferredCompanyId(items);
        this.selectedCompanyId = resolvedCompanyId;
        this.systemAdminContext.setCompanyId(resolvedCompanyId || null);

        if (!resolvedCompanyId) {
          this.selectedCompanyId = '';
          this.selectedClinicId = '';
          this.clinicOptions = [];
          this.systemAdminContext.setClinicId(null);
          return;
        }

        this.loadClinics(resolvedCompanyId);
      },
      error: () => {
        this.companyOptions = [];
      },
    });
    this.subscriptions.add(sub);
  }

  private loadTenantClinics(): void {
    const sub = this.settingsService.getClinicOperationalSettings().subscribe({
      next: (items) => {
        this.clinicOptions = items;
        const resolvedClinicId = this.resolvePreferredClinicId(items);
        this.selectedClinicId = resolvedClinicId;
        this.systemAdminContext.setClinicId(resolvedClinicId || null);
      },
      error: () => {
        this.clinicOptions = [];
        this.selectedClinicId = '';
        this.systemAdminContext.setClinicId(null);
      },
    });
    this.subscriptions.add(sub);
  }

  private loadClinics(companyId: string): void {
    const sub = this.settingsService.getClinicOperationalSettings(companyId).subscribe({
      next: (items) => {
        this.clinicOptions = items;
        const resolvedClinicId = this.resolvePreferredClinicId(items);
        this.selectedClinicId = resolvedClinicId;
        this.systemAdminContext.setClinicId(resolvedClinicId || null);
      },
      error: () => {
        this.clinicOptions = [];
        this.selectedClinicId = '';
        this.systemAdminContext.setClinicId(null);
      },
    });
    this.subscriptions.add(sub);
  }

  private get isCompanyRequiredForCurrentView(): boolean {
    const path = this.currentPath;
    if (!path) {
      return false;
    }

    if (path.startsWith('/notifications/templates') || path.startsWith('/notification-templates') || path.startsWith('/security')) {
      return !this.isGlobalScope;
    }

    if (
      path.startsWith('/appointments')
      || path.startsWith('/slots')
      || path.startsWith('/devices')
      || path.startsWith('/downtime')
      || path.startsWith('/specialist-downtime')
      || path.startsWith('/notifications')
      || path.startsWith('/users')
      || path.startsWith('/procedure-types')
      || path.startsWith('/settings')
      || path.startsWith('/clinics')
      || path.startsWith('/data-retention')
    ) {
      return true;
    }

    return false;
  }

  private get isClinicRequiredForCurrentView(): boolean {
    const path = this.currentPath;
    if (!path) {
      return false;
    }

    return path.startsWith('/devices');
  }

  private normalizePath(url: string): string {
    const [rawPath] = (url ?? '').split('?');
    const path = (rawPath || '/').trim();

    const segments = path
      .split('/')
      .filter((segment) => segment.length > 0);
    if (segments.length === 0) {
      return '/';
    }

    if (
      segments.length >= 2
      && !this.isKnownRootRoute(segments[0])
      && this.isKnownRootRoute(segments[1])
    ) {
      return `/${segments.slice(1).join('/')}`;
    }

    return `/${segments.join('/')}`;
  }

  private isKnownRootRoute(segment: string): boolean {
    return [
      'appointments',
      'slots',
      'downtime',
      'specialist-downtime',
      'notifications',
      'notification-queue',
      'notification-templates',
      'users',
      'settings',
      'clinics',
      'companies',
      'devices',
      'data-retention',
      'security',
      'login',
      'my-profile',
      'tickets',
      'technicians',
      'dashboard',
    ].includes(segment);
  }

  private resolvePreferredCompanyId(items: CompanyItem[]): string {
    const selected = this.selectedCompanyId.trim();
    if (selected && items.some((item) => item.companyId === selected)) {
      return selected;
    }

    const fromContext = this.systemAdminContext.getCompanyId()?.trim() ?? '';
    if (fromContext && items.some((item) => item.companyId === fromContext)) {
      return fromContext;
    }

    if (fromContext) {
      const bySlug = items.find((item) => item.slug === fromContext);
      if (bySlug) {
        return bySlug.companyId;
      }
    }

    return '';
  }

  private resolvePreferredClinicId(items: ClinicOperationalSettingsDto[]): string {
    const selected = this.selectedClinicId.trim();
    if (selected && items.some((item) => item.clinicId === selected)) {
      return selected;
    }

    const fromContext = this.systemAdminContext.getClinicId()?.trim() ?? '';
    if (fromContext && items.some((item) => item.clinicId === fromContext)) {
      return fromContext;
    }

    return '';
  }
}
